import { useEffect, useState } from "react";
import { CreditCard, Search } from "lucide-react";
import { api, fmtDate, ghs } from "../../lib/api";
import { EmptyState, PageHeader, StatusBadge } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";
import { Modal, Table, Td } from "./kit";

const FILTERS = ["", "success", "pending", "failed", "cancelled"];

const TxDetail = ({ reference }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/transactions/${reference}`).then((r) => setD(r.data)).catch(() => setD({ transaction: {}, events: [] })); }, [reference]);
  if (!d) return <FullLoader />;
  const t = d.transaction;
  const rows = [["Reference", t.reference], ["Customer", t.user_phone], ["Item", `${t.item_name} (${t.item_type})`], ["Amount", ghs(t.amount_minor)], ["Status", t.status], ["Mode", t.mode], ["Provider code", t.provider_code || "—"], ["Provider reason", t.provider_reason || "—"], ["Created", fmtDate(t.created_at, true)], ["Verified", fmtDate(t.verified_at, true)]];
  return (
    <div className="space-y-6" data-testid="tx-detail">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {rows.map(([k, v]) => (<div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="mt-0.5 break-all font-mono-rx text-slate-200">{v}</dd></div>))}
      </dl>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Payment events</p>
        <ol className="space-y-2 border-l border-white/10 pl-4 text-xs">
          {d.events.map((e, i) => (<li key={i}><span className="font-mono-rx text-emerald-300">{e.event_type}</span> <span className="text-slate-500">· {e.source} · {fmtDate(e.created_at, true)}</span></li>))}
        </ol>
      </div>
    </div>
  );
};

export default function AdminPayments() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/transactions", { params: { status, q } }).then((r) => setD(r.data)).catch(() => setD({ items: [], total: 0 })), 250);
    return () => clearTimeout(t);
  }, [status, q]);

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin" title="Payments" text={d ? `${d.total} transaction(s)` : ""} />
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          {FILTERS.map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`tx-filter-${s || "all"}`}
              className={`rounded-full border px-4 py-2 text-sm capitalize transition-colors ${status === s ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200" : "border-white/10 text-slate-400 hover:text-white"}`}>{s || "All"}</button>
          ))}
        </div>
        <label className="relative md:w-80">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Reference, phone or item" className="input-rx pl-10" data-testid="tx-search-input" />
        </label>
      </div>
      {!d ? <FullLoader /> : d.items.length === 0 ? <EmptyState icon={CreditCard} testId="admin-tx-empty" title="No transactions" text="Payments will appear here once customers check out." /> : (
        <Table head={["Reference", "Customer", "Item", "Amount", "Date", "Status"]} testId="admin-tx-table" minW="820px">
          {d.items.map((t) => (
            <tr key={t.reference} onClick={() => setOpen(t.reference)} className="cursor-pointer transition-colors hover:bg-white/[0.02]" data-testid={`admin-tx-row-${t.reference}`}>
              <Td className="font-mono-rx text-slate-300">{t.reference}</Td><Td className="font-mono-rx text-slate-400">{t.user_phone}</Td>
              <Td className="text-white">{t.item_name}</Td><Td className="font-mono-rx text-amber-400">{ghs(t.amount_minor)}</Td>
              <Td className="text-slate-400">{fmtDate(t.created_at, true)}</Td><Td><StatusBadge status={t.status} /></Td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={!!open} onClose={() => setOpen(null)} title="Transaction" testId="tx-modal">{open && <TxDetail reference={open} />}</Modal>
    </div>
  );
}
